import * as R from 'ramda';
import React from 'react';
import {withRouter} from 'react-router-dom';
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';
import {arrayMoveImmutable} from 'array-move';

import {translate} from 'iss/strings';
import {siteSizes as allSiteSizes} from 'iss/service/ActivationDataService';
import {createForm, getHeadingsFromExcel, modifyForm, subscribeToForm} from 'iss/service/FormService';
import {minimumGradeValue} from 'iss/service/GradingScaleService';
import {mapIndexed, swapDown, swapUp} from 'iss/utils/functional';
import Box from 'iss/widgets/Box';
import {Button, CancelButton, RemoveButton, SubmitButton} from 'iss/widgets/Buttons';
import {FlexItem, FlexRow} from 'iss/widgets/Flex';
import {Checkbox, Field, FileInput, FormSection, FormWithState, Input, RadioGroup, Select, TextArea, ValidationErrorBox} from 'iss/widgets/Forms';
import GradingScaleReportingInfo from 'iss/widgets/GradingScaleReportingInfo';
import {ErrorBox, RestResultMessage, SuccessBox} from 'iss/widgets/MessageBox';
import {
  ActivationReportingStyleSelector,
  AnswerVisibilitySelector,
  FormContextSelector,
  GradingScaleSelector,
  ServiceTypeSelector,
  SiteSizeSelector,
  SiteTypeSelector
} from 'iss/widgets/Selectors';
import {ConditionalFormSection} from 'iss/widgets/form/ConditionalFormSection';
import validators, {validatorFrom} from 'iss/widgets/form/validation/validators';
import { hasAdminRole, hasExtAdminRole } from '../service/PartyService';

const emptyHeading = {
  title: '',
  questions: []
};
const emptyQuestion = {
  name: "",
  description: "",
  weight: 1,
};

const FormEditor = withRouter(subscribeToForm(class FormEditor extends React.Component {
  state = {
    backendError: null,
  };
  static getDerivedStateFromProps({form: {isPublic, personEvaluationContext, deleteThresholdMonths, ...rest}}) {
    return {
      initialForm: {
        deleteThresholdMonths: deleteThresholdMonths ? deleteThresholdMonths : null,
        visibility: isPublic ? "public" : "private",
        personEvaluationContext: personEvaluationContext || "NOT_IN_USE",
        personEvaluationAnswerVisibility: "EVALUATOR_AND_SUBJECT",
        excelUpload: {},
        alternativeTitles: [null, null, null],
        ...rest
      }
    };
  }

  isMobifiable = () => hasAdminRole() || hasExtAdminRole();

  saveForm  = ({visibility, personEvaluationContext, gradingScale, aimedGrade, siteTypes, serviceTypes, headings, siteSizes, ...form}) => {
    const addSortOrder = mapIndexed((obj, index) => R.assoc('sortOrder', index, obj));
    //null == Any site size
    if (R.includes(null, siteSizes)) {
      siteSizes = allSiteSizes;
    }

    const data = {
      id: this.props.form.id,
      ...R.dissoc("excelUpload", R.dissoc("showDeleteThreshold", form)),
      isPublic: visibility === "public",
      personEvaluationContext: personEvaluationContext === "NOT_IN_USE" ? null : personEvaluationContext,
      gradingScaleId: gradingScale.id,
      siteTypes: siteTypes.map(R.prop("id")),
      serviceTypes: serviceTypes.map(R.prop("id")),
      siteSizes,
      aimedGradeId: aimedGrade ? aimedGrade.id : null,
      headings: headings.map(({id, title, questions}, index) => ({
        id, title,
        questions: addSortOrder(questions),
        sortOrder: index,
      }))
    };

    const save = this.isNew() ? createForm : modifyForm;
    save(data).subscribe((result) => this.props.history.push("/forms/"+ result.id + "?success=true"),
                         (errorResponse) => this.setState({backendError: errorResponse}));
  };
  isNew = () => {
    return !this.props.form.id;
  };
  render() {
    const showSuccessMessage = this.props.history.location.search.indexOf("success=true") != -1;

    return <FormWithState initialValues={this.state.initialForm} className="tight-form-controls" onSubmit={this.saveForm}>
      {(values, formApi) =>
        <React.Fragment>
          <SuccessBox id="submitSuccess" message={showSuccessMessage ? translate('common.savedSuccessfully') : null} />
          <h1>{this.isNew() ? translate('formEditor.newTitle') : translate('formEditor.editTitle')}</h1>

          <Input label="formEditor.formName" id="form-name" valuePath="name" validate={validators.required} maxLength="255" showCounter={true} disabled={!this.isMobifiable()} />

          <Input label="formEditor.formDescription" id="form-description" valuePath="description" maxLength="2000" showCounter={true} disabled={!this.isMobifiable()} />

          <Checkbox id="showDeleteThreshold" className="text-nowrap" label="formEditor.showDeleteThreshold" valuePath="showDeleteThreshold" disabled={!this.isMobifiable()} />
          <ConditionalFormSection showWhen={() => values.showDeleteThreshold == true}>
            <Input label="formEditor.answerDeleteThreshold" id="form-delete-threshold" valuePath="deleteThresholdMonths" validate={validators.integer} disabled={!this.isMobifiable()} />
          </ConditionalFormSection>

          <h2>{translate('formEditor.filteringOptions')}</h2>

          <FormContextSelector label="formEditor.usedInPersonEvaluation" valuePath="personEvaluationContext" inline id="personEvaluationContext" disabled={!this.isMobifiable()} />
          <ConditionalFormSection showWhen={() => values.personEvaluationContext !== 'NOT_IN_USE'}>
            <Box>
              <AnswerVisibilitySelector label="formEditor.answerVisibility" valuePath="personEvaluationAnswerVisibility" inline id="answerVisibility" disabled={!this.isMobifiable()} />
            </Box>
          </ConditionalFormSection>

          <Checkbox label="formEditor.usedInSiteEvaluation" valuePath="usedInSiteEvaluation" disabled={!this.isMobifiable()} />
          <ConditionalFormSection showWhen={R.prop('usedInSiteEvaluation')}>
            <Box>
              <RadioGroup label="formEditor.visibilityInQualityAssurance" id="form-visibility" valuePath="visibility" inline
                options={[
                  {value: "public", label: translate("formEditor.public")},
                  {value: "private", label: translate("formEditor.private")}
                ]}
                disabled={!this.isMobifiable()} />
              <FlexRow flexAllChildren>
                <SiteSizeSelector multi={true} label="formEditor.siteSizes" id="siteSizes" valuePath="siteSizes"
                  clearable={true} validate={validators.chooseAtLeastOne} disabled={!this.isMobifiable()} />
                <SiteTypeSelector multi={true} label="formEditor.siteTypes" id="siteTypes" valuePath="siteTypes"
                  clearable={true} validate={validators.chooseAtLeastOne} disabled={!this.isMobifiable()} />
                <ServiceTypeSelector multi={true} label="formEditor.serviceTypes" id="serviceTypes" valuePath="serviceTypes"
                  clearable={true} validate={validators.chooseAtLeastOne} disabled={!this.isMobifiable()} />
              </FlexRow>
            </Box>
          </ConditionalFormSection>

          <h2>{translate('formEditor.gradingScale')}</h2>

          <FlexRow flexAllChildren>
            <GradingScaleSelector label="formEditor.gradingScale" id="gradingScale"
              onChange={() => formApi.setValue("aimedGrade", null)}
              valuePath="gradingScale" clearable={false} validate={validators.chooseOne}
              disabled={!this.isMobifiable()} />
            <AimedGradeChooser scale={values.gradingScale} valuePath="aimedGrade" disabled={!this.isMobifiable()} />
          </FlexRow>

          <ConditionalFormSection showWhen={R.prop('usedInSiteEvaluation')}>
            <ActivationReportingStyleSelector label="formEditor.activationReportingStyle" inline
              id="reportingStyle" valuePath="reportingStyle" disabled={!this.isMobifiable()} />
            <p>{translate('formEditor.activationReportingStyleExplanation.'+values.reportingStyle)}</p>

            <ValidationErrorBox id="reportingStyleValidation"
              validate={[
                validatorFrom( ({aimedGrade, reportingStyle}) =>
                  reportingStyle == 'GRADE_AVERAGE' || aimedGrade != null,
                  "formEditor.percentageReportingNeedsAimedGrade"
                ),
                validatorFrom( ({aimedGrade, gradingScale, reportingStyle}) =>
                  reportingStyle != 'QUALITY_PERCENTAGE' || (aimedGrade && aimedGrade.numericValue != minimumGradeValue(gradingScale)),
                  "formEditor.chooseHigherAimedGrade"
                ),
              ]} />
          </ConditionalFormSection>
            <ConditionalFormSection showWhen={({reportingStyle}) => reportingStyle == 'QUALITY_PERCENTAGE'}>
              <FlexRow flexAllChildren>
                <Input type="number" step="any" min="0" label="formEditor.minQualityPercent" id="minQualityPercent"
                  valuePath="minQualityPercent" validate={validators.number} disabled={!this.isMobifiable()} />
                <Input type="number" step="any" min="0" label="formEditor.maxQualityPercent" id="maxQualityPercent"
                  valuePath="maxQualityPercent" validate={validators.number} disabled={!this.isMobifiable()} />
              </FlexRow>
            </ConditionalFormSection>
          <ConditionalFormSection showWhen={R.prop('usedInSiteEvaluation')}>
            <GradingScaleReportingInfo {...values} />
          </ConditionalFormSection>

          <h3>{translate('formEditor.remarksFieldNames')}</h3>
          <p>{translate('formEditor.remarksFieldNamesExplanation')}</p>

          <FlexRow flexAllChildren>
            <Input label="formEditor.remarksFieldName1" id="form-remarks-field-name-1"
              valuePath={['remarksFieldNames', 0]} placeholder={translate('formEditor.remarksFieldName1Default')}
              maxLength="500" showCounter={true} disabled={!this.isMobifiable()} />
            <Input label="formEditor.remarksFieldName2" id="form-remarks-field-name-2"
              valuePath={['remarksFieldNames', 1]} placeholder={translate('formEditor.remarksFieldName2Default')}
              maxLength="500" showCounter={true} disabled={!this.isMobifiable()} />
          </FlexRow>

          <FlexRow flexAllChildren>
            <Input label="formEditor.additionalInfoFieldName" id="form-additional-info-field-name"
                   valuePath={['alternativeInfoFieldNames', 0]} placeholder={translate('formEditor.additionalInfoFieldNameDefault')}
                   maxLength="500" showCounter={true} disabled={!this.isMobifiable()} />
            <Input label="formEditor.infoClarificationFieldName" id="form-info-clarification-field-name"
                   valuePath={['alternativeInfoFieldNames', 1]} placeholder={translate('formEditor.infoClarificationFieldNameDefault')}
                   maxLength="500" showCounter={values.usedInSiteEvaluation} disabled={!this.isMobifiable() || !values.usedInSiteEvaluation}
                   helpText={values.usedInSiteEvaluation ? '' : 'formEditor.fieldOnlyUsedInSiteEvaluation'} />
          </FlexRow>

          <FlexRow flexAllChildren>
            <Input label="formEditor.alternativeReportTitle" id="form-alternative-report-title"
              valuePath={['alternativeTitles', 0]} placeholder={translate('formEditor.alternativeReportTitleDefault')}
              maxLength="500" showCounter={true} disabled={!this.isMobifiable()} />
            <Input label="formEditor.alternativeEventLogTitle" id="form-alternative-event-log-title"
              valuePath={['alternativeTitles', 1]} placeholder={translate('formEditor.alternativeEventLogTitleDefault')}
              maxLength="500" showCounter={true} disabled={!this.isMobifiable()} />
            <Input label="formEditor.alternativeQuestionTitle" id="form-alternative-question-title"
              valuePath={['alternativeTitles', 2]} placeholder={translate('formEditor.alternativeQuestionTitleDefault')}
              maxLength="500" showCounter={true} disabled={!this.isMobifiable()} />
          </FlexRow>

          <Box>
            <h3>{translate('formEditor.excelUpload.uploadFromFile')}</h3>
            <FileUploader onUpload={(excelRead) => {
              this.setState({excelErrorMessage: excelRead.message});
              formApi.setValue("headings", excelRead.headings);
            }} onError={(error) => {
              this.setState({excelErrorMessage: `${translate('formEditor.excelUpload.readError')}: ${error.message}` });
            }} disabled={!this.isMobifiable()} />
            <ErrorBox id="excel-upload-error" message={this.state.excelErrorMessage} />
          </Box>

          <FormHeadings headings={values.headings} disabled={!this.isMobifiable()} />

          <RestResultMessage id="backendError" result={this.state.backendError} />
          <Button id="new-heading" className="pull-right" disabled={!this.isMobifiable()}
            onClick={() => formApi.updateValue("headings", R.append(emptyHeading))}>
            {translate('formEditor.newHeading')}
          </Button>
          <div className="form-group">
            <SubmitButton id="save-form" text={translate('common.save')} disabled={!this.isMobifiable()} />
            <CancelButton link="/forms" />
          </div>
        </React.Fragment>
      }
    </FormWithState>;
  }
}));

function FormHeadings({headings, disabled}) {
  const headingCount = headings.length;

  return <React.Fragment>
    <div id="headings" className="clearfix">
      <h2>{translate('formEditor.headings')}</h2>
      {headings.map((heading, headingNr) =>
        <FormHeadingSection key={headingNr} heading={heading} headingNr={headingNr} headingCount={headingCount} disabled={disabled} />)
      }
    </div>
    <ValidationErrorBox id="formValidation"
      validate={[ validatorFrom( form => form.headings.length >= 1, "formEditor.shouldHaveHeadings"), ]} />
  </React.Fragment>;
}

function FormHeadingSection({heading, headingNr, headingCount, disabled}) {
  const isFirst = headingNr === 0;
  const isLast = headingNr === headingCount-1 ;
  const title = heading.title || translate('formEditor.untitledHeading');

  return <div className="panel panel-default">
    <div className="panel-heading">
      <Field valuePath="headings" pure={true}>
        {fieldApi =>
            <div className="pull-right">
              <Button id={"move-heading-"+headingNr+"-up"} size="xs" disabled={isFirst || disabled}
                icon="arrow-up" alt="" onClick={() => fieldApi.updateValue(swapDown(headingNr))} />
              <Button id={"move-heading-"+headingNr+"-down"} size="xs" disabled={isLast || disabled}
                icon="arrow-down" alt="" onClick={() => fieldApi.updateValue(swapUp(headingNr))} />
              <RemoveButton id={"remove-heading-"+headingNr} size="xs"
                onClick={() => fieldApi.updateValue(R.remove(headingNr, 1))} disabled={disabled} />
            </div>
        }
      </Field>
      {title}
    </div>
    <FormSection valuePath={["headings", headingNr]} pure={true}>
      {(values, formSectionApi) =>
        <div className="panel-body">
          <Input label="formEditor.headingName" id={"heading-title-"+headingNr} valuePath="title" validate={validators.required}
          maxLength="255" showCounter={true} disabled={disabled}/>
          <label>{translate('formEditor.questions')}</label>
          <QuestionList items={heading.questions} headingNr={headingNr}
            useDragHandle={true} disabled={disabled}
            onSortEnd={({oldIndex, newIndex}) => {
              formSectionApi.updateValue("questions", (qs) => arrayMoveImmutable(qs, oldIndex, newIndex));
            }}
            onRemove={(questionNumber) => {
              formSectionApi.updateValue("questions", R.remove(questionNumber, 1));
            }} />
          <Button id={"new-question-for-"+headingNr} className="pull-right" disabled={disabled}
            onClick={() => {
              formSectionApi.updateValue("questions", R.append(emptyQuestion));
            }}>
            {translate('formEditor.newQuestion')}
          </Button>
        </div>
      }
    </FormSection>
  </div>;
}

const QuestionList = SortableContainer(({items, headingNr, onRemove, disabled}) => {
  return <div className="heading">
    {R.defaultTo([], items).map((value, index) => (
      <Question key={index} index={index} value={value} headingNr={headingNr} questionNr={index} onRemove={onRemove} disabled={disabled} childrenDisabled={disabled} />
    ))}
    <ValidationErrorBox id={"headingValidation-"+headingNr} validate={[
        validatorFrom( heading => heading.questions.length >= 1, "formEditor.shouldHaveQuestions"),
    ]} />
  </div>;
});

const QuestionSortHandle = SortableHandle(() =>
  <div className="btn btn-default">
    <span className="glyphicon glyphicon-move" aria-hidden="true" />
  </div>
);
const Question = SortableElement(({headingNr, questionNr, onRemove, childrenDisabled}) => {
  // SortableElement doesn't pass property disabled to the child collection, thus we use a non-standard prop.
  const idBase = `heading-${headingNr}-question-${questionNr}`;
  return <FormSection key={questionNr} valuePath={['questions', questionNr]} pure={true}>
    <FlexRow flexAllChildren className="question" alignItems="center">
      <Input id={idBase} label="formEditor.questionName"
        valuePath="name" validate={validators.required}
        maxLength="500" showCounter={true} disabled={childrenDisabled} />
      <TextArea id={`${idBase}-description`} label="formEditor.questionDescription"
        valuePath="description" rows="2"
        maxLength="2000" showCounter={true} disabled={childrenDisabled} />
      <Input flex="none" id={`${idBase}-weight`} label="formEditor.questionWeight"
        type="number" valuePath="weight" validate={validators.required} disabled={childrenDisabled} />
      <FlexItem flex="none">
        <QuestionSortHandle />
        <Button id={"remove-question-"+headingNr+"-"+questionNr} disabled={childrenDisabled}
          buttonLook="danger" icon="remove" onClick={() => onRemove(questionNr)} />
      </FlexItem>
    </FlexRow>
  </FormSection>;
});

function AimedGradeChooser({scale, valuePath, disabled}) {
  const availableGrades = scale ? scale.grades : [];
  const aimedGradeOptions = R.prepend(
    {
      value: null,
      label: translate('formEditor.chooseAimedGrade'),
    }, availableGrades.map((grade) => ({
      value: grade,
      label: `${grade.text}`
    }))
  );
  return <Select id="aimedGrade" label="formEditor.aimedGrade" valuePath={valuePath} options={aimedGradeOptions} disabled={disabled} />;
}

/** NOTE: this does not work in Microsoft Edge as of April 2019 */
function FileUploader({onUpload, onError, disabled}) {
  return <FormSection valuePath="excelUpload">
      {(values) =>
    <React.Fragment>
      <FileInput label="formEditor.excelUpload.fileInput" id="excel-file-input" valuePath="excelFile" disabled={disabled} />
      <Input label="formEditor.excelUpload.sheetname" helpText="formEditor.excelUpload.helpText" id="form-sheetname" valuePath="sheetname" disabled={disabled} />
      <Button id="submit-excel-file" className="submit-file"
        onClick={() => getHeadingsFromExcel(values.excelFile, values.sheetname ? values.sheetname : '').subscribe(onUpload, onError)}
        disabled={!values.excelFile || !values.excelFile.name || disabled}
        >
        {translate('formEditor.excelUpload.submitExcelFile')}
      </Button>
    </React.Fragment>}
  </FormSection>;
}

export default FormEditor;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/FormEditor.jsx